import React, { useEffect, useMemo, useRef, useState } from "react"
import { useDebounce } from "react-use"
import Parse from "parse"
import { Box, Stack, TableRow, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, TextField } from "@mui/material"
import { makeStyles } from "@mui/styles"
import { Field, FieldArray, Formik } from "formik"
import AddIcon from "@mui/icons-material/Add"
import CloseIcon from "@mui/icons-material/Close"
import WarningAmberIcon from "@mui/icons-material/WarningAmber"
import HelpOutlineIcon from "@mui/icons-material/HelpOutline"
import { StyledTable, StyledHeaderCell, StyledBodyCell, StyledFooterCell } from "../../../components/styled/StyledTableComponents"
import { COLORS } from "../../../utils"
import IconCheck from "../../../components/Supplier/IconCheck"
import IconClose from "../../../components/Supplier/IconClose"
import ConformityTooltip from "./ConformityTooltip"
import MoreMenu from "../../../components/MoreMenu"

const notEmpty = (value) => value !== null && value !== undefined && value !== ""
const isNumeric = (value) => !isNaN(value) && !isNaN(parseFloat(value))

const matchO2 = (value) => isNumeric(value) && value >= 0 && value <= 1
const matchCO2 = (value) => isNumeric(value) && value >= 40 && value <= 51

const ModalGazTest = ({ onClose, open, packagingExecution, currentStep, onSave, rows = 3 }) => {
  const classes = useStyles()
  const conformities = useRef([])
  const [conformityStatus, setConformityStatus] = useState({})
  
  const defaultRow = {
    placement: "",
    o2: "",
    co2: "",
  }

  const defaultValue = {
    rows: Array.from({ length: rows }, (_, index) => ({ ...defaultRow, placement: (index + 1) })),
    author: Parse.User.current().get("username") || "",
  }

  const getConformityClass = (row, gas, testIndex, rowIndex) => {
    const value = row[gas]
    const isFilled = notEmpty(value)
    const isInvalid = !conformities.current[testIndex]?.[rowIndex]?.[gas]

    return isFilled ? isInvalid ? classes.conformityInvalid : classes.conformityValid : ""
  }

  return (
    <Dialog
      open={open}
      aria-labelledby="Modal-dialog-gaz-title"
      sx={{ "& .MuiDialog-paper": { width: 700, maxWidth: 700 } }}
    >
      <DialogTitle id="modal-download-production" sx={{ mt: 2, pb: 1 }} className="relative">
        <div style={{ fontSize: "24px", lineHeight: "32px", color: COLORS.BLACK800 }}>
          {packagingExecution.uniqueCode} - {packagingExecution.recipeName}
          <div style={{ fontSize: "16px", lineHeight: "24px", fontWeight: 400 }}>{packagingExecution.packagingLine?.name}</div>
        </div>
        <IconButton
          aria-label="close"
          onClick={onClose}
          size="large"
          sx={{ position: "absolute", right: 4, top: 4, color: COLORS.BLACK800 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Formik
        initialValues={{
          tests: [
            ...(packagingExecution.gazTest ?? []),
            defaultValue
          ]
        }}
        validate={(values) => {
          const errors = {}
          if (!values.tests || values.tests.length === 0) {
            errors.tests = "At least one test is required"
          } else {
            const lastTestIndex = values.tests.length - 1
            const lastTest = values.tests[lastTestIndex]
            if (lastTest && lastTest.rows && lastTest.rows.length === 0) {
              errors.conformities = "Au moins un emplacement est requis."
            } else if (lastTest && !lastTest.rows.every(c => matchO2(c.o2) && matchCO2(c.co2))) {
              errors.conformities = values.tests[lastTestIndex].rows.some(row => !isNumeric(row.o2) || !isNumeric(row.co2)) ? "empty" : "Invalid conformity values"
            }
          }
          return errors
        }}

        onSubmit={(values) => {
          onSave({
            tests: values.tests.map((test, testIndex) => ({
              ...test,
              createdAt: test.createdAt ?? new Date(),
              user: test.user ? {
                __type: "Pointer",
                className: "_User",
                objectId: test.user.objectId
              } : Parse.User.current(),
              step: test.createdAt ? test.step : currentStep || "BEFORE_START",
              rows: test.rows.map((row, rowIndex) => ({
                ...row,
                conformity: conformities.current[testIndex][rowIndex],
              }))
            }))
          })
          onClose()
        }}
        validateOnChange={false}
        validateOnBlur={true}
        validateOnMount
      >
        {({ handleSubmit, values, isValid = false, validateForm, errors, setFieldValue }) => {

          useEffect(() => {
            conformities.current = values.tests.map(test => (test.rows.map(row => ({
              o2: isNumeric(row.o2) && matchO2(row.o2),
              co2: isNumeric(row.co2) && matchCO2(row.co2)
            }))
            ))
          }, [values.tests])

          // Debounce validation on change
          useDebounce(() => {
            validateForm(values)
          }, 500, [values, validateForm])

          const isFullFilled = useMemo(() => values.tests[values.tests.length - 1].rows.every(row => notEmpty(row.o2) && notEmpty(row.co2)), [values.tests])

          const handleBlur = (testIdx, rowIdx) => {
            const row = values.tests[testIdx]?.rows[rowIdx]
            // Only update status if both fields are filled
            if (notEmpty(row?.o2) && notEmpty(row?.co2)) {
              const isConform = isNumeric(row.o2) && matchO2(row.o2) && isNumeric(row.co2) && matchCO2(row.co2)
              setConformityStatus(prev => ({
                ...prev,
                [testIdx]: {
                  ...(prev[testIdx] || {}),
                  [rowIdx]: {
                    shown: true,
                    isConform
                  }
                }
              }))
            }
          }

          return <form onSubmit={handleSubmit}>
            <DialogContent sx={{ pt: 1 }}>
              {errors.conformities && errors.conformities !== "empty" && (
                <div className={`flexRow ${classes.warningBlock}`}>
                  <WarningAmberIcon className={classes.warningIcon} sx={{ color: "#EF6C00", fontSize: 24, marginRight: "16px" }} />
                  <div>Votre test n’est pas conforme, veuillez en réaliser un nouveau.<br />
                    Si vous avez des questions, vous pouvez vous référer aux instructions de mise sous atmosphère protectrice.
                  </div>
                </div>)}
              <FieldArray name="tests">
                {({ push }) => (
                  <>
                    <div className={"flexRow spaceBetween center"} style={{ marginBottom: "16px", color: COLORS.BLACK_700, fontSize: "16px", lineHeight: "24px" }}>
                      <div> Complétez le test gaz.<br />En cas de long série, faites un test tous les 600 plats </div>
                      <Button
                        onClick={() => push({ ...defaultValue, author: values.tests[values.tests.length - 1].author })}
                        variant="text"
                        startIcon={<AddIcon />}
                        sx={{ textTransform: "initial" }}
                        disabled={!isFullFilled}
                      > NOUVEAU TEST </Button>
                    </div>

                    <Stack spacing={3}>
                    {values.tests && values.tests.map((test, index) => {
                      // Skip tests that are already done (have createdAt)
                      if (test.createdAt) return null
                      const isLastIndex = index === values.tests.length - 1
                      return (
                        <FieldArray key={index} name={`tests.${index}.rows`}>
                          {({ remove: removeRow }) => (
                            <StyledTable>
                              <colgroup> {/* Column width repartition */}
                                <col style={{ width: "147px" }} />
                                <col style={{ width: "147px" }} />
                                <col style={{ width: "147px" }} />
                                <col style={{ width: "166px" }} />
                                <col style={{ width: "36px" }} />
                              </colgroup>
                              {/* HEADER */}
                              <thead>
                                <TableRow>
                                  <StyledHeaderCell leftEdge>Emplacement</StyledHeaderCell>
                                  <StyledHeaderCell>O₂ % (valeur)</StyledHeaderCell>
                                  <StyledHeaderCell>CO₂ % (valeur)</StyledHeaderCell>
                                  <StyledHeaderCell>
                                    <Box sx={{ display: "flex", alignItems: "center" }}>
                                      Conformité
                                      <ConformityTooltip>
                                        <HelpOutlineIcon sx={{ ml: 1, cursor: "pointer", fontSize: 18 }} />
                                      </ConformityTooltip>
                                    </Box>
                                  </StyledHeaderCell>
                                  <StyledHeaderCell rightEdge />
                                </TableRow>
                              </thead>

                              {/* BODY */}
                              <tbody>
                                {values.tests[index].rows.map((row, rowIndex) => (
                              <TableRow key={rowIndex}>
                                {/* Placement */}
                                <StyledBodyCell leftEdge>
                                  {isLastIndex ? (
                                    <Field name={`tests.${index}.rows.${rowIndex}.placement`}>
                                      {({ field }) => (
                                        <TextField
                                          {...field}
                                          select
                                          fullWidth
                                          className={classes.fieldInputPlacement}
                                        >
                                          {Array.from({ length: rows }, (_, i) => i + 1).map((row) => (
                                            <MenuItem key={row} value={row}>
                                              {row}
                                            </MenuItem>
                                          ))}
                                        </TextField>
                                      )}
                                    </Field>
                                  ) : (
                                    <div>{row.placement}</div>
                                  )}
                                </StyledBodyCell>
                                {/* O₂ */}
                                <StyledBodyCell
                                  className={getConformityClass(row, "o2", index, rowIndex)}
                                  padding="12px 16px"
                                >
                                  {isLastIndex ? (
                                    <Field name={`tests.${index}.rows.${rowIndex}.o2`}>
                                      {({ field }) => (
                                        <Stack direction="row" alignItems="center" gap={1}>
                                          <TextField
                                            {...field}
                                            type="number"
                                            size="small"
                                            inputProps={{
                                              inputMode: "numeric",
                                              pattern: "[0-9]*",
                                            }}
                                            className={classes.fieldInput}
                                            onBlur={() => handleBlur(index, rowIndex)}
                                          />
                                          <span className="suffix">%</span>
                                        </Stack>
                                      )}
                                    </Field>
                                  ) : (
                                    <div className="conformityValue">{row.o2} <span className="suffix">%</span></div>
                                  )}
                                </StyledBodyCell>
                                {/* CO₂ */}
                                <StyledBodyCell
                                  className={getConformityClass(row, "co2", index, rowIndex)}
                                  padding="12px 16px"
                                >
                                  {isLastIndex ? (
                                    <Field name={`tests.${index}.rows.${rowIndex}.co2`}>
                                      {({ field }) => (
                                        <Stack direction="row" alignItems="center" gap={1}>
                                          <TextField
                                            {...field}
                                            type="number"
                                            size="small"
                                            inputProps={{
                                              inputMode: "numeric",
                                              pattern: "[0-9]*",
                                            }}
                                            className={classes.fieldInput}
                                            onBlur={() => handleBlur(index, rowIndex)}
                                          />
                                          <span className="suffix">%</span>
                                        </Stack>
                                      )}
                                    </Field>
                                  ) : (
                                    <div className="conformityValue">{row.co2} <span className="suffix">%</span></div>
                                  )}
                                </StyledBodyCell>
                                {/* Conformity */}
                                <StyledBodyCell padding="12px 16px">
                                  {conformityStatus[index]?.[rowIndex]?.shown && (
                                    conformityStatus[index][rowIndex].isConform ? (
                                      <Box className={classes.conformityRow}>
                                        <IconCheck bgColor="green" color="white" />
                                        <span>Conforme</span>
                                      </Box>
                                    ) : (
                                      <Box className={classes.conformityRow}>
                                        <IconClose bgColor="red" color="white" />
                                        <span>Non conforme</span>
                                      </Box>
                                    )
                                  )}
                                </StyledBodyCell>
                                <StyledBodyCell className={classes.menuRow} rightEdge>
                                  {rowIndex > 0 &&
                                    <MoreMenu
                                      menus={[
                                        {
                                          onClick: () => {
                                            removeRow(rowIndex)
                                            // Update placement numbers for remaining rows after deletion
                                            setTimeout(() => {
                                              const remainingRows = values.tests[index].rows.filter((_, idx) => idx !== rowIndex)
                                              remainingRows.forEach((row, idx) => {
                                                setFieldValue(`tests.${index}.rows.${idx}.placement`, idx + 1)
                                              })
                                              validateForm()
                                            }, 0)
                                          },
                                          label: "Supprimer l'emplacement"
                                        }
                                      ]}
                                      iconButtonProps={{sx: { padding: "4px", marginY: "4px" }}}
                                      menuProps={{
                                        anchorOrigin: {
                                          vertical: "bottom",
                                          horizontal: "center"
                                        },
                                        transformOrigin: {
                                          vertical: "top",
                                          horizontal: "right"
                                        },
                                        PaperProps: {
                                          sx: {
                                            padding: "8px 0px",
                                            "& .MuiList-root": { padding: 0 },
                                            "& .MuiButtonBase-root": { padding: "0 8px", fontSize: "14px", lineHeight: "22px" },
                                          }
                                        }
                                      }}
                                    />
                                  }
                                </StyledBodyCell>
                              </TableRow>
                            ))}

                            {/* FOOTER */}
                            <TableRow>
                              <StyledFooterCell colSpan={5}>
                                <Field name={`tests.${index}.author`}>
                                  {({ field }) => (
                                    <TextField
                                      label="Fait par"
                                      variant="standard"
                                      disabled={!isLastIndex}
                                      className={classes.AuthorField}
                                      {...field}
                                    />
                                  )}
                                </Field>
                              </StyledFooterCell>
                            </TableRow>
                          </tbody>
                        </StyledTable>
                          )}
                        </FieldArray>
                      )
                    })}
                    </Stack>
                  </>
                )}
              </FieldArray>
            </DialogContent>

            <DialogActions classes={{ root: classes.rootDialogActions }}>
              <Button
                onClick={onClose}
              >
                Retour
              </Button>

              <Button
                onClick={handleSubmit}
                color="primary"
                variant="contained"
                disabled={!isValid}
              >
                Valider
              </Button>
            </DialogActions>
          </form>
        }}
      </Formik>
    </Dialog >
  )
}
const useStyles = makeStyles(() => ({
  fieldInput: {
    "& .MuiInputBase-input ": {
      textAlign: "center",
      paddingTop: "4px",
      paddingBottom: "4px",
      maxWidth: "84px",
      height: "32px",
    },
    "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
      WebkitAppearance: "none",
      margin: 0,
    },
    "& input[type=number]": {
      MozAppearance: "textfield"
    },
    "& .MuiOutlinedInput-root fieldset": {
      borderRadius: "8px",
      border: "1px solid" + COLORS.BLACK_500
    },
  },
  fieldInputPlacement: {
    "& .MuiInputBase-input ": {
      paddingTop: "4px",
      paddingBottom: "4px",
      paddingLeft:"16px",
      height: "24px",
      fontSize:"14px",
      lineHeight:"24px"
    },
    "& .MuiOutlinedInput-root fieldset": {
      borderRadius: "43px",
      border: "1px solid" + COLORS.BLACK_600
    },
    "& .MuiSvgIcon-root": {
      color: COLORS.BLACK_600,
    }
  },
  rootDialogActions: {
    justifyContent: "space-between",
    padding: "8px 24px 32px",
  },
  conformityRow: {
    display: "flex",
    direction: "row",
    "& span": {
      marginLeft: "8px"
    },
  },
  conformityInvalid: {
    "& .conformityValue, & .suffix, & .MuiOutlinedInput-root fieldset": {
      color: COLORS.RED_600,
      borderColor: COLORS.RED_600,
      "&:hover": { borderColor: COLORS.RED_600 }
    }
  },
  conformityValid: {
    "& .suffix": {
      color: COLORS.BLACK_500,
    },
    "& .conformityValue": {
      color: COLORS.BLACK800,
    }
  },
  warningBlock: {
    fontSize: "16px",
    lineHeight: "24px",
    margin: "16px 0",
    backgroundColor: "#FFF4E5",
    borderRadius: "8px",
    padding: 16,
  },
  menuRow: {
    display: "flex", 
    justifyContent: "flex-end",
    padding: "16px 16px 16px 0px",
    minHeight: "65px"
  },
  AuthorField: {
    width: "264px",
    "& .MuiInputBase-input": {
      fontSize: "14px",
      lineHeight: "22px",
      color: COLORS.BLACK800,
      marginTop: "3px",
      marginBottom: "7px",
      padding: 0
    },
    "& .MuiFormLabel-root": {
      fontSize: "14px",
      lineHeight: "22px",
      color: COLORS.BLACK_500,
    },
    "& .MuiInputBase-root:before, & .MuiInputBase-root:after": {
      borderBottom: "1px solid " + COLORS.BLACK_500
    }
  }
}))

export default ModalGazTest